<template>
  <div>
    <NavBar pageName="Validity Option Limits" />
    <v-container style="margin-top: 185px;">
      <v-btn variant="text" size="small" @click="$router.push('/settings/client-services')" class="mb-2">
        <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
        Client Services Settings
      </v-btn>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-shield-check</v-icon>
            Limite Optiuni Valabilitate
          </span>
          <div>
            <v-btn color="secondary" variant="outlined" class="mr-2" @click="loadLimits">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon class="mr-1">mdi-plus</v-icon>
              Adauga Limite
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Limitele controleaza valorile minime si maxime pe care distribuitorii le pot seta pentru optiunile de valabilitate.
            Daca un distribuitor nu are limite definite, se folosesc limitele parintelui sau, pana la root.
          </v-alert>

          <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
            <strong>Limite implicite (cand nu exista limite definite):</strong>
            Credit: {{ defaultLimits.min_credit }} - {{ defaultLimits.max_credit }} EUR |
            Zile: {{ defaultLimits.min_days }} - {{ defaultLimits.max_days }}
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="limits"
            :loading="loading"
            :items-per-page="20"
          >
            <template #[`item.credit_range`]="{ item }">
              <v-chip color="warning" size="small">
                {{ item.min_credit }} - {{ item.max_credit }} EUR
              </v-chip>
            </template>
            <template #[`item.days_range`]="{ item }">
              <v-chip color="info" size="small">
                {{ item.min_days }} - {{ item.max_days }} zile
              </v-chip>
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'grey'" size="small">
                {{ item.active ? 'Activ' : 'Inactiv' }}
              </v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
              <v-btn size="x-small" color="error" @click="confirmDelete(item)">
                <v-icon size="small">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="formDialog" max-width="600">
        <v-card>
          <v-card-title :class="isEditing ? 'bg-primary' : 'bg-success'" class="text-white">
            <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? 'Editeaza Limite' : 'Adauga Limite Noi' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <!-- Distributor Selection -->
            <v-autocomplete
              v-if="!isEditing"
              v-model="form.client_id"
              :items="distributors"
              item-title="name"
              item-value="id"
              label="Distribuitor"
              :rules="[v => !!v || 'Distribuitorul este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
            />
            <div v-else class="mb-3">
              <strong>Distribuitor:</strong> {{ form.client_name }}
            </div>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.min_credit"
                  label="Credit minim (EUR)"
                  type="number"
                  min="0.01"
                  step="0.01"
                  :rules="[v => v > 0 || 'Valoare invalida']"
                  density="compact"
                  variant="outlined"
                  prefix="EUR"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.max_credit"
                  label="Credit maxim (EUR)"
                  type="number"
                  min="0.01"
                  step="0.01"
                  :rules="[v => v > 0 && v >= form.min_credit || 'Trebuie >= min']"
                  density="compact"
                  variant="outlined"
                  prefix="EUR"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.min_days"
                  label="Zile minim"
                  type="number"
                  min="1"
                  :rules="[v => v > 0 || 'Valoare invalida']"
                  density="compact"
                  variant="outlined"
                  suffix="zile"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.max_days"
                  label="Zile maxim"
                  type="number"
                  min="1"
                  :rules="[v => v > 0 && v >= form.min_days || 'Trebuie >= min']"
                  density="compact"
                  variant="outlined"
                  suffix="zile"
                />
              </v-col>
            </v-row>

            <!-- Active -->
            <v-checkbox
              v-model="form.active"
              label="Limite active"
              density="compact"
              class="mb-3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="formDialog = false">Anuleaza</v-btn>
            <v-btn
              :color="isEditing ? 'primary' : 'success'"
              @click="saveLimit"
              :loading="saving"
              :disabled="!isFormValid"
            >
              {{ isEditing ? 'Salveaza' : 'Adauga' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="bg-error text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Confirma Stergerea
          </v-card-title>
          <v-card-text class="pt-4">
            Sigur doriti sa stergeti limitele pentru distribuitorul
            <strong>{{ limitToDelete?.client_name }}</strong>?
            <br><br>
            <v-alert type="warning" density="compact">
              Dupa stergere, distribuitorul va folosi limitele parintelui sau sau cele implicite.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="deleteDialog = false">Anuleaza</v-btn>
            <v-btn color="error" @click="deleteLimit" :loading="deleting">Sterge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import {
  getValidityOptionLimits,
  createValidityOptionLimit,
  updateValidityOptionLimit,
  deleteValidityOptionLimit
} from '@/composables/useValidityPurchaseOptionsAPI'
import store from '@/store'

export default {
  name: 'ValidityOptionLimitsSettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      limits: [],
      distributors: [],
      defaultLimits: {
        min_credit: 1,
        max_credit: 100,
        min_days: 1,
        max_days: 365
      },
      headers: [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Distribuitor', key: 'client_name' },
        { title: 'Credit (EUR)', key: 'credit_range', width: '180px' },
        { title: 'Zile', key: 'days_range', width: '150px' },
        { title: 'Status', key: 'active', width: '100px' },
        { title: 'Actiuni', key: 'actions', width: '120px', sortable: false }
      ],
      // Form dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      form: {
        id: null,
        client_id: null,
        client_name: '',
        min_credit: 1,
        max_credit: 100,
        min_days: 1,
        max_days: 365,
        active: true
      },
      // Delete dialog
      deleteDialog: false,
      deleting: false,
      limitToDelete: null,
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    token() {
      return store.getters.getToken
    },
    isFormValid() {
      return this.form.client_id &&
        this.form.min_credit > 0 && this.form.max_credit >= this.form.min_credit &&
        this.form.min_days > 0 && this.form.max_days >= this.form.min_days
    }
  },
  async mounted() {
    await this.loadLimits()
  },
  methods: {
    async loadLimits() {
      this.loading = true
      try {
        const result = await getValidityOptionLimits(this.token)
        if (!result.error) {
          this.limits = result.limits || []
          this.distributors = result.distributors || []
          if (result.default_limits) {
            this.defaultLimits = result.default_limits
          }
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.loading = false
      }
    },
    openAddDialog() {
      this.isEditing = false
      this.form = {
        id: null,
        client_id: null,
        client_name: '',
        min_credit: this.defaultLimits.min_credit,
        max_credit: this.defaultLimits.max_credit,
        min_days: this.defaultLimits.min_days,
        max_days: this.defaultLimits.max_days,
        active: true
      }
      this.formDialog = true
    },
    openEditDialog(limit) {
      this.isEditing = true
      this.form = {
        id: limit.id,
        client_id: limit.client_id,
        client_name: limit.client_name,
        min_credit: limit.min_credit,
        max_credit: limit.max_credit,
        min_days: limit.min_days,
        max_days: limit.max_days,
        active: limit.active
      }
      this.formDialog = true
    },
    async saveLimit() {
      if (!this.isFormValid) return

      this.saving = true
      try {
        let result
        if (this.isEditing) {
          result = await updateValidityOptionLimit(this.token, this.form.id, {
            min_credit: this.form.min_credit,
            max_credit: this.form.max_credit,
            min_days: this.form.min_days,
            max_days: this.form.max_days,
            active: this.form.active
          })
        } else {
          result = await createValidityOptionLimit(this.token, {
            client_id: this.form.client_id,
            min_credit: this.form.min_credit,
            max_credit: this.form.max_credit,
            min_days: this.form.min_days,
            max_days: this.form.max_days,
            active: this.form.active
          })
        }

        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.formDialog = false
          await this.loadLimits()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.saving = false
      }
    },
    confirmDelete(limit) {
      this.limitToDelete = limit
      this.deleteDialog = true
    },
    async deleteLimit() {
      if (!this.limitToDelete) return

      this.deleting = true
      try {
        const result = await deleteValidityOptionLimit(this.token, this.limitToDelete.id)
        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.deleteDialog = false
          await this.loadLimits()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.deleting = false
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>
